'use client';

import { useEffect, useRef, useState } from 'react';
import { Code as CodeIcon } from 'lucide-react';

import type { CodeBlock } from '@impact/types';
import { cn } from '@/lib/utils';

interface CodeBlockRendererProps {
  block: CodeBlock;
  editing?: boolean;
}

const DEBOUNCE_MS = 100;
const DEFAULT_MAX_HEIGHT = 5000;

const BACKGROUND_CLASS = {
  transparent: '',
  muted: 'bg-muted/40',
  card: 'bg-card border-border',
} as const;

export function CodeBlockRenderer({ block, editing }: CodeBlockRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(400);
  const {
    html,
    caption,
    background = 'transparent',
    padded = false,
    rounded = true,
    border = false,
    maxHeight,
  } = block.data;

  const cap = maxHeight ?? DEFAULT_MAX_HEIGHT;

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'resize' && typeof e.data.height === 'number') {
        setHeight(Math.min(e.data.height, cap));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [cap]);

  const frameClasses = cn(
    'relative overflow-hidden',
    BACKGROUND_CLASS[background],
    padded && 'p-6',
    rounded && 'rounded-xl',
    border && 'border border-border',
  );

  if (!html) {
    if (!editing) return null;
    return (
      <div className="my-12">
        <div
          className={cn(
            frameClasses,
            'border-2 border-dashed border-muted-foreground/30 bg-muted/30',
          )}
        >
          <div className="flex min-h-[10rem] flex-col items-center justify-center gap-2 px-6 py-10 text-center text-muted-foreground">
            <CodeIcon className="h-8 w-8 opacity-50" />
            <p className="text-sm font-medium">Embed code</p>
            <p className="text-xs text-muted-foreground/70">
              Paste an iframe, script, or HTML snippet in the “…” menu &rarr;{' '}
              <em>HTML / embed script</em>.
            </p>
          </div>
        </div>
        {(caption || editing) && (
          <p
            className="mt-2 inline-block min-w-[4rem] text-sm text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Caption']"
            data-editable="data.caption"
          >
            {caption}
          </p>
        )}
      </div>
    );
  }

  const srcdoc = `<!DOCTYPE html>
<html><head>
<style>html, body { margin: 0; overflow: hidden; }</style>
</head><body>
<div id="cw">${html}</div>
<script>
var wrapper = document.getElementById('cw');
var last = 0;
var timer;
function report() {
  clearTimeout(timer);
  timer = setTimeout(function() {
    var h = Math.max(wrapper.offsetHeight, wrapper.scrollHeight);
    if (h > 0 && h !== last) {
      last = h;
      window.parent.postMessage({ type: 'resize', height: h }, '*');
    }
  }, ${DEBOUNCE_MS});
}
new ResizeObserver(report).observe(wrapper);
var checks = 0;
var poll = setInterval(function() {
  report();
  if (++checks >= 20) clearInterval(poll);
}, 500);
report();
</script>
</body></html>`;

  return (
    <div className="my-12">
      <div className={frameClasses}>
        <iframe
          ref={iframeRef}
          srcDoc={srcdoc}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-popups-to-escape-sandbox"
          style={{ width: '100%', height, border: 'none' }}
          title={caption || 'Embedded content'}
          className={cn(editing && 'pointer-events-none')}
        />
      </div>
      {(caption || editing) && (
        <p
          className="mt-2 inline-block min-w-[4rem] text-sm text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Caption']"
          {...(editing ? { 'data-editable': 'data.caption' } : {})}
        >
          {caption}
        </p>
      )}
    </div>
  );
}
